import React, { useEffect, useState } from "react";
import { Modal, Select, Textarea, Button, StatusBadge, LoadingBlock, toast } from "@/components/ds";
import { api, formatApiError, type Estimate } from "@/lib/api";
import { STAGE_META, STAGE_ORDER, PRIORITIES, PRIORITY_TONE, ACTIVITY_LABEL, fmtDate } from "./constants";
import { EstimateManager } from "../estimates/EstimateManager";
import { EST_STATUS, amountText } from "../estimates/constants";

export function LeadProfile({ leadId, onClose, onChanged }: { leadId: string; onClose: () => void; onChanged: () => void }) {
  const [data, setData] = useState<any>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [showEstimate, setShowEstimate] = useState<{ estimateId?: string; open: boolean }>({ open: false });

  const load = () => api.crmLeadGet(leadId).then(setData).catch((e) => toast.error(formatApiError(e)));
  const loadEstimates = () => api.estimatesList({ lead_id: leadId }).then(setEstimates).catch(() => {});
  useEffect(() => { load(); loadEstimates(); /* eslint-disable-next-line */ }, [leadId]);

  const setStage = async (stage: string) => {
    setBusy(true);
    try { await api.crmLeadStage(leadId, stage); await load(); onChanged(); toast.success("Stage updated"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const setPriority = async (priority: string) => {
    setBusy(true);
    try { await api.crmLeadUpdate(leadId, { priority }); await load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.crmLeadNote(leadId, note.trim()); setNote(""); await load(); toast.success("Note added"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const archive = async () => {
    setBusy(true);
    try { await api.crmLeadArchive(leadId); toast.success("Lead archived"); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const startRecovery = async () => {
    setBusy(true);
    try { await api.recoveryEnroll(leadId); toast.success("Lead enrolled in recovery"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Lead profile" description="Manage stage, notes and activity." testid="lead-profile">
      {!data ? <LoadingBlock rows={5} /> : (
        <div className="max-h-[72vh] space-y-5 overflow-y-auto pr-1">
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="lead-profile-contact">
            <p className="text-base font-semibold text-slate-900">{data.lead.contact_name}</p>
            <p className="text-sm text-slate-500">{data.contact?.phone ?? "No phone"}{data.contact?.email ? ` · ${data.contact.email}` : ""}</p>
            <p className="mt-1 text-xs text-slate-400">{data.lead.requested_service ?? "No service"} · source: {data.lead.source} · est. ${Number(data.lead.estimated_value ?? 0).toLocaleString()}</p>
          </div>
          <PortalLinkCard contactId={data.contact?.id ?? data.lead?.contact_id} />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Stage</p>
              <Select value={data.lead.stage} onChange={setStage} options={STAGE_ORDER.map((s) => ({ value: s, label: STAGE_META[s].label }))} testid="lead-stage-select" />
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-slate-500">Priority</p>
              <Select value={data.lead.priority} onChange={setPriority} options={PRIORITIES} testid="lead-priority-select" />
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-slate-800">Add note</p>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a note…" data-testid="lead-note-input" />
            <div className="mt-2 flex justify-end"><Button size="sm" onClick={addNote} disabled={busy} data-testid="lead-note-add">Add note</Button></div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-800">Estimates</p>
              <Button size="sm" variant="outline" onClick={() => setShowEstimate({ open: true })} data-testid="lead-add-estimate-btn">Add / Track Estimate</Button>
            </div>
            {estimates.length === 0 ? (
              <p className="text-xs text-slate-400" data-testid="lead-no-estimates">No estimates yet for this customer.</p>
            ) : (
              <div className="space-y-1.5" data-testid="lead-estimates-list">
                {estimates.map((e) => {
                  const sm = EST_STATUS[e.status] ?? EST_STATUS.estimate_requested;
                  return (
                    <button key={e.id} onClick={() => setShowEstimate({ open: true, estimateId: e.id })} data-testid={`lead-estimate-${e.id}`}
                      className="flex w-full items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-left hover:bg-slate-50">
                      <span className="text-sm text-slate-700">{amountText(e.amount, e.amount_disclosed)}{e.service ? ` · ${e.service}` : ""}</span>
                      <span className="flex items-center gap-2">
                        {e.followup_state === "active" && <StatusBadge tone="blue">follow-up</StatusBadge>}
                        <StatusBadge tone={sm.tone}>{sm.label}</StatusBadge>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <p className="mb-2 text-sm font-semibold text-slate-800">Activity timeline</p>
            <ol className="space-y-2" data-testid="lead-activity-timeline">
              {data.activities.map((a: any) => (
                <li key={a.id} className="flex items-start gap-3 rounded-lg border border-slate-100 px-3 py-2" data-testid={`activity-${a.id}`}>
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                  <div className="min-w-0">
                    <p className="text-sm text-slate-800">{ACTIVITY_LABEL[a.activity_type] ?? a.activity_type}{a.description ? `: ${a.description}` : ""}</p>
                    <p className="text-xs text-slate-400">{fmtDate(a.created_at)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="flex justify-between border-t border-slate-100 pt-4">
            <Button variant="ghost" onClick={archive} disabled={busy} data-testid="lead-archive-btn">Archive lead</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={startRecovery} disabled={busy} data-testid="lead-recovery-btn">Start recovery</Button>
              <Button variant="outline" onClick={onClose}>Close</Button>
            </div>
          </div>
        </div>
      )}
      {showEstimate.open && (
        <EstimateManager estimateId={showEstimate.estimateId} leadId={leadId}
          onClose={() => setShowEstimate({ open: false })} onSaved={loadEstimates} />
      )}
    </Modal>
  );
}

function PortalLinkCard({ contactId }: { contactId?: string }) {
  const [cfg, setCfg] = useState<{ included: boolean } | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.portalConfig().then((c) => setCfg(c)).catch(() => setCfg({ included: false }));
    if (contactId) api.portalGetLink(contactId).then((r) => setLink(r.link)).catch(() => {});
  }, [contactId]);
  if (!cfg) return null;
  if (!cfg.included) return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3 text-xs text-slate-500" data-testid="portal-upsell">
      Customer self-service portal is available on Pro and Custom plans.
    </div>
  );
  const gen = async () => {
    if (!contactId) { toast.error("No linked customer record"); return; }
    setBusy(true);
    try { const r = await api.portalCreateLink(contactId); setLink(r.link); toast.success("Portal link ready"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3" data-testid="portal-link-card">
      <p className="mb-2 text-sm font-semibold text-slate-800">Customer portal</p>
      {link ? (
        <div className="flex flex-wrap items-center gap-2">
          <input readOnly value={link} className="min-w-0 flex-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600" data-testid="portal-link-value" />
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="portal-link-copy">Copy</Button>
          <Button size="sm" variant="ghost" onClick={gen} disabled={busy} data-testid="portal-link-rotate">Rotate</Button>
        </div>
      ) : (
        <Button size="sm" onClick={gen} disabled={busy} data-testid="portal-link-generate">Generate secure portal link</Button>
      )}
    </div>
  );
}
